import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/components/flexkit/Pagination";
import { cn } from "@/components/podkit/lib/cn";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { usePaginationShortcuts } from "@/hooks/use-pagination-shortcuts";
import { useCallback, useMemo, type FC } from "react";
import { useLocation } from "react-router-dom";

export type AdaptivePaginationProps = {
    /** Callback for page navigation (paginated mode) - receives 0-based page number */
    onPageChange?: (page: number) => void;
    /** Loading state */
    isLoading?: boolean;
    /** Additional CSS classes */
    className?: string;
    /** Current items being displayed (for "Showing X to Y" text) */
    currentItemsCount?: number;
    /** Page size for calculating item ranges */
    pageSize?: number;
    /** Total count of items */
    totalCount?: number | undefined;
    /** Total pages */
    totalPages: number;
    /** Current page */
    currentPage: number;
};

/**
 * Adaptive pagination component that renders full pagination controls for page-based navigation
 */
export const AdaptivePagination: FC<AdaptivePaginationProps> = ({
    onPageChange,
    isLoading = false,
    className,
    currentItemsCount = 0,
    pageSize = 25,
    totalCount,
    totalPages,
    currentPage,
}) => {
    const location = useLocation();

    // Build URL with updated page parameter while preserving other query params
    const buildPageUrl = useMemo(() => {
        return (page: number) => {
            const params = new URLSearchParams(location.search);
            const urlPage = page + 1; // Convert 0-based to 1-based for URL
            if (urlPage === 1) {
                params.delete("page");
            } else {
                params.set("page", String(urlPage));
            }
            const query = params.toString();
            return `${location.pathname}${query ? `?${query}` : ""}`;
        };
    }, [location.pathname, location.search]);

    const { isMobileViewport } = useIsMobileViewport();

    const visiblePages = useMemo<("ellipsis" | number)[]>(() => {
        if (typeof totalCount !== "number") return [];

        const siblingCount = isMobileViewport ? 0 : 1;
        const totalNumbers = siblingCount * 2 + 5; // sibling + current + sibling + first + last + 2*ellipsis

        // Case 1: If pages are fewer than max visible buttons, show all
        if (totalPages <= totalNumbers) {
            return Array.from({ length: totalPages }, (_, i) => i);
        }

        const leftSiblingIndex = Math.max(currentPage - siblingCount, 0);
        const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages - 1);

        // We show dots if there is more than 1 number between boundary and sibling
        const shouldShowLeftDots = leftSiblingIndex > 2;
        const shouldShowRightDots = rightSiblingIndex < totalPages - 3;

        const firstPageIndex = 0;
        const lastPageIndex = totalPages - 1;

        // Case 2: No Left Dots (We are near the start)
        // Output: [0, 1, 2, 3, 4, ..., 10]
        if (!shouldShowLeftDots && shouldShowRightDots) {
            const leftItemCount = 3 + 2 * siblingCount;
            const leftRange = Array.from({ length: leftItemCount }, (_, i) => i);
            return [...leftRange, "ellipsis", lastPageIndex];
        }

        // Case 3: No Right Dots (We are near the end)
        // Output: [0, ..., 6, 7, 8, 9, 10]
        if (shouldShowLeftDots && !shouldShowRightDots) {
            const rightItemCount = 3 + 2 * siblingCount;
            const rightRange = Array.from({ length: rightItemCount }, (_, i) => totalPages - rightItemCount + i);
            return [firstPageIndex, "ellipsis", ...rightRange];
        }

        // Case 4: Both Dots (We are in the middle)
        // Output: [0, ..., 6, 7, 8, ..., 10]
        if (shouldShowLeftDots && shouldShowRightDots) {
            const middleRange = Array.from(
                { length: rightSiblingIndex - leftSiblingIndex + 1 },
                (_, i) => leftSiblingIndex + i,
            );
            return [firstPageIndex, "ellipsis", ...middleRange, "ellipsis", lastPageIndex];
        }

        return [];
    }, [totalCount, isMobileViewport, totalPages, currentPage]);

    // Calculate item range text
    const itemRangeText = useMemo(() => {
        if (typeof totalCount !== "number") {
            return currentItemsCount > 0 ? `Showing ${currentItemsCount} results` : "";
        }

        const start = currentPage * pageSize + 1;
        const end = Math.min((currentPage + 1) * pageSize, totalCount);

        return `Showing ${start.toLocaleString()} to ${end.toLocaleString()} of ${totalCount.toLocaleString()} result${totalCount === 1 ? "" : "s"}`;
    }, [currentItemsCount, currentPage, pageSize, totalCount]);

    // Keyboard navigation for paginated mode
    const handlePrevious = useCallback(() => {
        if (currentPage > 0 && onPageChange) {
            onPageChange(currentPage - 1);
        }
    }, [currentPage, onPageChange]);

    const handleNext = useCallback(() => {
        if (currentPage < totalPages - 1 && onPageChange) {
            onPageChange(currentPage + 1);
        }
    }, [currentPage, totalPages, onPageChange]);

    usePaginationShortcuts({
        onPrevious: handlePrevious,
        onNext: handleNext,
        enabled: typeof totalCount === "number" && !isLoading,
    });

    if (totalCount === 0) {
        return null;
    }

    return (
        <div
            className={cn(
                "flex w-full flex-col items-center justify-between gap-4 py-8 sm:flex-row sm:px-6",
                className,
            )}
        >
            {/* Item range text */}
            <div className="flex-shrink text-sm text-content-secondary">{itemRangeText}</div>

            {/* Pagination controls */}
            <Pagination className="w-fit-content mx-0 w-auto">
                <PaginationContent>
                    {/* Previous button */}
                    <PaginationItem>
                        <PaginationPrevious
                            href={currentPage > 0 ? buildPageUrl(currentPage - 1) : undefined}
                            onClick={(e) => {
                                if (currentPage > 0 && onPageChange) {
                                    e.preventDefault();
                                    onPageChange(currentPage - 1);
                                }
                            }}
                            aria-disabled={currentPage === 0}
                            className={cn({ "pointer-events-none opacity-50": currentPage === 0 })}
                            data-tracking-id="pagination-previous"
                        />
                    </PaginationItem>

                    {/* Page numbers */}
                    {visiblePages.map((page, index) => (
                        <PaginationItem key={`page-${index}`}>
                            {page === "ellipsis" ? (
                                <PaginationEllipsis />
                            ) : (
                                <PaginationLink
                                    href={buildPageUrl(page)}
                                    onClick={(e) => {
                                        if (onPageChange) {
                                            e.preventDefault();
                                            onPageChange(page);
                                        }
                                    }}
                                    isActive={page === currentPage}
                                    data-tracking-id={`pagination-page-${page + 1}`}
                                >
                                    {page + 1}
                                </PaginationLink>
                            )}
                        </PaginationItem>
                    ))}

                    {/* Next button */}
                    <PaginationItem>
                        <PaginationNext
                            href={currentPage < totalPages - 1 ? buildPageUrl(currentPage + 1) : undefined}
                            onClick={(e) => {
                                if (currentPage < totalPages - 1 && onPageChange) {
                                    e.preventDefault();
                                    onPageChange(currentPage + 1);
                                }
                            }}
                            aria-disabled={currentPage >= totalPages - 1}
                            className={cn({ "pointer-events-none opacity-50": currentPage >= totalPages - 1 })}
                            data-tracking-id="pagination-next"
                        />
                    </PaginationItem>
                </PaginationContent>
            </Pagination>
        </div>
    );
};
